// Knowledge Base Master Dashboard.
//
// Shows counts across all 7 databases, recent activity, recommended next
// actions, and a universal search bar (keyword + AI toggle).

import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Search,
  Sparkles,
  ArrowRight,
  Brain,
  Loader2,
  Activity,
  ClipboardList,
  Wand2,
  Plus,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { KB_DATABASES, DB_BY_KEY } from "./kbConfig";

// Parse hints like "DexaFit (vendor)" → {hint: "DexaFit", db_key: "vendors"}
// Supports both singular ("vendor") and plural ("vendors") + the literal db keys.
const DB_KEY_ALIASES = {
  program: "programs", programs: "programs",
  vendor: "vendors", vendors: "vendors",
  clinic: "clinics", clinics: "clinics",
  workflow: "workflows", workflows: "workflows",
  marketing: "marketing", campaign: "marketing", campaigns: "marketing",
  economics: "economics", economic: "economics",
  competitor: "competitors", competitors: "competitors", competition: "competitors",
};

function parseSearchHint(query) {
  const m = query.match(/^(.+?)\s*\(([a-zA-Z]+)\)\s*$/);
  if (!m) return { hint: query.trim(), db_key: null };
  const inferred = DB_KEY_ALIASES[m[2].toLowerCase()];
  return { hint: m[1].trim(), db_key: inferred || null };
}

function StatCard({ db, count }) {
  const Icon = db.icon;
  return (
    <Link
      to={`/admin/knowledge-base/${db.key}`}
      data-testid={`kb-stat-${db.key}`}
      className={`group relative overflow-hidden rounded-2xl border border-white/10 bg-slate-950/60 p-5 ring-1 ${db.cls.ring} transition ${db.cls.hoverBorder} hover:bg-slate-900/70`}
    >
      <div className="flex items-center justify-between">
        <div className={`text-[11px] uppercase tracking-[0.16em] font-semibold ${db.cls.textSoft}`}>
          {db.label}
        </div>
        <Icon className={`h-4 w-4 ${db.cls.textSoft}`} />
      </div>
      <div className="mt-3 text-4xl font-bold text-slate-100" data-testid={`kb-stat-${db.key}-value`}>
        {count ?? "—"}
      </div>
      <div className="mt-1 text-[11px] text-slate-400 line-clamp-2">{db.description}</div>
      <div className="mt-3 inline-flex items-center gap-1 text-[11px] font-semibold text-slate-300 group-hover:text-white">
        Open <ArrowRight className="h-3 w-3" />
      </div>
    </Link>
  );
}

export default function AdminKnowledgeBase() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [q, setQ] = useState("");
  const [aiMode, setAiMode] = useState(false);
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);
  const [aiAnswer, setAiAnswer] = useState(null);
  const [generatingDb, setGeneratingDb] = useState(null); // db_key while AI runs

  const parsed = useMemo(() => parseSearchHint(q), [q]);
  // When to show the "Create as new record" panel: there's a query, we've
  // searched (results !== null), and either zero hits OR the user typed
  // an explicit "(db)" hint.
  const showCreatePanel =
    !!q.trim() && results !== null && !searching && (results.length === 0 || !!parsed.db_key);

  const generateRecord = async (db_key) => {
    setGeneratingDb(db_key);
    try {
      const r = await api.post("/kb/ai/generate-record", {
        db_key,
        hint: parsed.hint || q,
        save: true,
      });
      toast.success(`Created "${r.data.record?.[DB_BY_KEY[db_key].primary] || parsed.hint}"`);
      navigate(`/admin/knowledge-base/${db_key}/${r.data.record.id}`);
    } catch (e) {
      toast.error(formatApiError(e, "AI generation failed"));
    } finally {
      setGeneratingDb(null);
    }
  };

  useEffect(() => {
    api
      .get("/kb/dashboard")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e, "Could not load KB dashboard")))
      .finally(() => setLoading(false));
  }, []);

  const totalRecords = data?.total_records ?? 0;

  const runSearch = async (e) => {
    e?.preventDefault?.();
    if (!q.trim()) return;
    setSearching(true);
    setResults(null);
    setAiAnswer(null);
    try {
      if (aiMode) {
        const r = await api.post("/kb/ai-search", { query: q });
        setAiAnswer(r.data.answer);
        setResults(r.data.results || []);
      } else {
        const r = await api.post("/kb/search", { query: q });
        setResults(r.data.results || []);
      }
    } catch (err) {
      toast.error(formatApiError(err, "Search failed"));
    } finally {
      setSearching(false);
    }
  };

  const recentList = data?.recent || [];
  const actions = data?.recommended_actions || [];

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-6 space-y-7"
      data-testid="kb-dashboard"
    >
      {/* Header */}
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[10.5px] uppercase tracking-[0.16em] font-semibold text-sky-400">
            Intelligence · Internal
          </div>
          <h1 className="mt-1 text-3xl font-bold text-slate-100 flex items-center gap-3">
            <Brain className="h-7 w-7 text-sky-400" /> Nodera Knowledge Base
          </h1>
          <p className="mt-1 max-w-3xl text-sm text-slate-400">
            The institutional intelligence system for Nodera Health. Records here compound
            knowledge across platform development, vendor vetting, clinic enablement, sales,
            marketing, and strategy. Admin-only.
          </p>
        </div>
        <div className="flex flex-col items-end gap-3">
          <Link
            to="/admin/knowledge-base/reports"
            data-testid="kb-briefings-link"
            className="inline-flex items-center gap-1.5 rounded-md border border-sky-500/40 bg-sky-500/10 px-3 py-2 text-sm font-semibold text-sky-200 hover:bg-sky-500/20"
          >
            <ClipboardList className="h-3.5 w-3.5" /> Intelligence Briefings
          </Link>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wide text-slate-400">Total records</div>
            <div className="text-3xl font-bold text-slate-100" data-testid="kb-total-records">
              {totalRecords}
            </div>
          </div>
        </div>
      </header>

      {/* Search */}
      <section
        className="rounded-2xl border border-white/10 bg-slate-950/60 p-5"
        data-testid="kb-search-section"
      >
        <form onSubmit={runSearch} className="flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[260px] relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="kb-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={
                aiMode
                  ? "Ask anything — e.g. 'Which vendors are best for sleep programs in Texas?'"
                  : "Search programs, vendors, clinics… or type 'DexaFit (vendor)' to AI-create"
              }
              className="pl-9 bg-slate-900/60 border-white/10 text-slate-100 placeholder:text-slate-500"
            />
          </div>
          <label
            data-testid="kb-search-ai-toggle"
            className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-white/10 bg-slate-900/60 px-3 py-1.5 text-xs text-slate-200"
          >
            <input
              type="checkbox"
              checked={aiMode}
              onChange={(e) => setAiMode(e.target.checked)}
              className="accent-sky-500"
            />
            <Sparkles className="h-3.5 w-3.5 text-sky-400" /> AI search
          </label>
          <Button
            type="submit"
            data-testid="kb-search-submit"
            disabled={searching || !q.trim()}
          >
            {searching ? (
              <>
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Searching…
              </>
            ) : (
              "Search"
            )}
          </Button>
        </form>

        {aiAnswer && (
          <div
            data-testid="kb-ai-answer"
            className="mt-4 rounded-xl border border-sky-500/30 bg-sky-500/5 p-4"
          >
            <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-sky-300">
              AI Answer · Claude Sonnet 4.5
            </div>
            <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-slate-100">{aiAnswer}</p>
          </div>
        )}

        {results && results.length > 0 && (
          <ul
            data-testid="kb-search-results"
            className="mt-4 divide-y divide-white/5 rounded-xl border border-white/10 bg-slate-900/40"
          >
            {results.map((r) => {
              const db = DB_BY_KEY[r.db_key];
              const Icon = db?.icon || Activity;
              return (
                <li key={`${r.db_key}-${r.id}`}>
                  <Link
                    to={`/admin/knowledge-base/${r.db_key}/${r.id}`}
                    data-testid={`kb-result-${r.id}`}
                    className="flex items-start gap-3 px-4 py-3 hover:bg-slate-900/80"
                  >
                    <Icon className={`mt-0.5 h-4 w-4 ${db?.cls?.text || "text-sky-400"} flex-shrink-0`} />
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-slate-100">{r.title}</div>
                      <div className="text-[11px] text-slate-400">{r.db_label}</div>
                      {r.snippet && (
                        <div className="mt-1 line-clamp-2 text-xs text-slate-400">{r.snippet}</div>
                      )}
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
        {results && results.length === 0 && (
          <div className="mt-4 rounded-xl border border-dashed border-white/10 bg-slate-900/40 p-4 text-center text-xs text-slate-400">
            No records matched "{q}". Try fewer keywords or toggle AI search.
          </div>
        )}

        {showCreatePanel && (
          <div
            data-testid="kb-create-from-search"
            className="mt-4 rounded-xl border border-sky-500/30 bg-gradient-to-br from-sky-500/10 to-violet-500/5 p-4"
          >
            <div className="flex items-center gap-2 text-sky-200">
              <Wand2 className="h-4 w-4" />
              <span className="text-[11px] uppercase tracking-[0.16em] font-semibold">
                Create with AI · Claude Sonnet 4.5
              </span>
            </div>
            <p className="mt-1.5 text-sm text-slate-200">
              Pre-fill a new record for{" "}
              <span className="font-semibold text-slate-100">"{parsed.hint || q}"</span>
              {parsed.db_key ? (
                <>
                  {" "}as a{" "}
                  <span className="font-semibold text-slate-100">
                    {DB_BY_KEY[parsed.db_key].label}
                  </span>{" "}
                  record.
                </>
              ) : (
                <> — pick which database to add it to:</>
              )}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {(parsed.db_key ? [DB_BY_KEY[parsed.db_key]] : KB_DATABASES).map((d) => {
                const Icon = d.icon;
                const busy = generatingDb === d.key;
                const otherBusy = generatingDb !== null && !busy;
                return (
                  <button
                    key={d.key}
                    data-testid={`kb-create-as-${d.key}`}
                    onClick={() => generateRecord(d.key)}
                    disabled={busy || otherBusy}
                    className={`group inline-flex items-center gap-2 rounded-full border border-white/10 bg-slate-900/60 px-3 py-1.5 text-xs font-semibold transition ${
                      busy
                        ? "opacity-100"
                        : otherBusy
                        ? "opacity-40"
                        : `hover:bg-slate-900/90 ${d.cls.hoverBorder}`
                    } ${d.cls.textSoft}`}
                  >
                    {busy ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Icon className="h-3 w-3" />
                    )}
                    <span className="text-slate-100">
                      {busy ? "Claude is researching…" : `New ${d.label}`}
                    </span>
                    {!busy && !parsed.db_key && (
                      <Plus className="h-2.5 w-2.5 text-slate-400 group-hover:text-slate-200" />
                    )}
                  </button>
                );
              })}
            </div>
            <p className="mt-2 text-[10.5px] text-slate-500">
              Tip: type <code className="text-slate-300">"Name (vendor)"</code>,{" "}
              <code className="text-slate-300">"Name (clinic)"</code>, or{" "}
              <code className="text-slate-300">"Name (competitor)"</code> to skip the picker.
            </p>
          </div>
        )}
      </section>

      {/* DB Stat grid */}
      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {KB_DATABASES.map((d) => (
          <StatCard key={d.key} db={d} count={data?.counts?.[d.key]} />
        ))}
      </section>

      {/* Recent + Recommended */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Recent */}
        <div
          data-testid="kb-recent-panel"
          className="rounded-2xl border border-white/10 bg-slate-950/60 p-5"
        >
          <h3 className="text-sm font-semibold text-slate-100 flex items-center gap-2 mb-3">
            <Activity className="h-4 w-4 text-emerald-400" /> Recently updated
          </h3>
          {loading ? (
            <div className="text-xs text-slate-500">Loading…</div>
          ) : recentList.length === 0 ? (
            <div className="text-xs text-slate-500">No activity yet.</div>
          ) : (
            <ul className="divide-y divide-white/5">
              {recentList.map((r) => {
                const db = DB_BY_KEY[r.db_key];
                const Icon = db?.icon || Activity;
                return (
                  <li key={`${r.db_key}-${r.id}`}>
                    <Link
                      to={`/admin/knowledge-base/${r.db_key}/${r.id}`}
                      className="flex items-center gap-3 py-2 hover:opacity-80"
                    >
                      <Icon className={`h-4 w-4 ${db?.cls?.text || "text-sky-400"}`} />
                      <div className="flex-1 min-w-0">
                        <div className="truncate text-sm text-slate-100">{r.title}</div>
                        <div className="text-[10px] uppercase tracking-wide text-slate-500">
                          {r.db_label}
                        </div>
                      </div>
                      <ArrowRight className="h-3 w-3 text-slate-500" />
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Recommended Actions */}
        <div
          data-testid="kb-actions-panel"
          className="rounded-2xl border border-white/10 bg-slate-950/60 p-5"
        >
          <h3 className="text-sm font-semibold text-slate-100 flex items-center gap-2 mb-3">
            <ClipboardList className="h-4 w-4 text-amber-400" /> Recommended next actions
          </h3>
          {loading ? (
            <div className="text-xs text-slate-500">Loading…</div>
          ) : actions.length === 0 ? (
            <div className="text-xs text-slate-500">All caught up — no recommendations.</div>
          ) : (
            <ul className="space-y-2">
              {actions.map((a, i) => (
                <li
                  key={`${a.title}-${i}`}
                  data-testid={`kb-action-${i}`}
                  className="rounded-xl border border-white/10 bg-slate-900/50 p-3"
                >
                  <div className="flex items-center gap-2">
                    <span className="rounded-full bg-amber-500/15 text-amber-300 px-2 py-0.5 text-[10px] font-semibold">
                      {a.priority}
                    </span>
                    <Link
                      to={`/admin/knowledge-base/${a.db_key}`}
                      className="text-sm font-semibold text-slate-100 hover:text-sky-300"
                    >
                      {a.title}
                    </Link>
                  </div>
                  <p className="mt-1 text-xs text-slate-400">{a.rationale}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </div>
  );
}
